import React, { useState, useEffect, useContext } from 'react'
import { useQuery } from 'react-query'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'

import { useWithAuth } from '../../containers/App'
import { useNotifications } from '../../containers/Notifications'
import { normalizeSortState } from '../../components/Table/convertSortState'
import { isEmptyObject } from '../../utils/isEmptyObject'
import AddUser from '../../containers/Users/AddUser'
import EditUser from '../../containers/Users/EditUser'
import { AppContext } from '../../containers/App'

import Heading from '../../components/Heading'
import Button from '../../components/Button'
import Layout from '../../components/Layout'
import Search from '../Search'
import UnitUserList from './UnitUserList'
import type { ReadyProps, Units, User } from './utils'
// @ts-ignore @todo add apprioprate declaration file
import userIcon from '../../images/usersIcon.svg'
import { TablePlaceHolder } from '../../utils/withLoading'
import { Hidden } from '../../styles/Shared'

function UnitsTabPanel() {
  const user = useContext<User>(AppContext)
  const { data: currentUnit } = useQuery<Units, string>(
    `unit-${user.unitId}`,
    () =>
      withAuth(
        `/diagnostic/rest/unit/${user.unitId}`,
        {
          method: 'GET',
        },
        true
      ),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  )
  const { notify } = useNotifications()
  const withAuth = useWithAuth()
  const [addUserModalReady, setAddUserModalReady] = useState<boolean>(false)
  const [prefetch, setPrefetch] = useState<boolean>(false)
  const [editUserModalReady, setEditUserModalReady] = useState({})
  const [usersData, setUsersData] = useState({
    models: null,
    meta: null,
  })
  const [ready, setReady] = useState<ReadyProps>({
    patients: false,
    users: false,
  })
  const [usersState, setUsersState] = useState({
    sorts: {},
    meta: {
      limit: 10,
      page: 0,
      total: 0,
    },
    filters: { search: '' },
  })

  const handleModalStates = (value) => {
    setPrefetch(!prefetch)
    value === 'AddUser' && setAddUserModalReady(false)
    value === 'EditUser' && setEditUserModalReady({})
  }

  const onUserSortedChange = (sort) => {
    setUsersState({ ...usersState, sorts: sort })
    setPrefetch(!prefetch)
  }
  const onUserPageChange = (index) => {
    setUsersState({
      ...usersState,
      meta: { ...usersState.meta, page: index },
    })
    setPrefetch(!prefetch)
  }
  const onSearchChange = (search) => {
    setUsersState((prevState) => ({
      ...prevState,
      filters: { ...prevState.filters, search },
      meta: { ...prevState.meta, page: 0 },
    }))
    setPrefetch(!prefetch)
  }

  const fetchUsers = async () => {
    setReady({ ...ready, users: false })
    const result = await withAuth(`/user/rest/user/search`, {
      method: 'POST',
      payload: {
        filters: {
          ...usersState.filters,
          unitId: user.unitId,
          roleName: 'diagnostician',
        },
        meta: {
          ...usersState.meta,
          sorts: normalizeSortState(usersState.sorts),
        },
      },
    })
    setReady({ ...ready, users: true })
    return result
  }

  const fetchData = async () => {
    try {
      const resultUsers = await fetchUsers()
      setUsersData(resultUsers)
    } catch {
      notify('Nie udało się pobrać użytkowników', 'error')
    }
  }
  useEffect(() => {
    fetchData()
  }, [prefetch])
  return (
    <Layout>
      <Helmet htmlAttributes={{ lang: 'pl-PL' }}>
        <title>Diagności</title>
      </Helmet>
      <Heading title={currentUnit ? currentUnit.name : ''}>
        <HeaderWrapper>
          <div style={{ width: '100%' }}>
            <label>
              <Search
                placeholder="Szukaj"
                ariaLabel={
                  usersState.filters.search
                    ? `Szukaj liczba znalezionych wyników: ${
                        usersData.models != null ? usersData.models.length : 0
                      }`
                    : 'Szukaj'
                }
                input={{
                  onChange: onSearchChange,
                  value: '',
                }}
              />
            </label>
          </div>

          <Button
            link={null}
            marginLeft={true}
            icon={userIcon}
            label="Dodaj osobę"
            onClick={() => setAddUserModalReady(true)}
            color="white"
            altTag=""
            imgAriaHidden={true}
            srText=""
          />
        </HeaderWrapper>
      </Heading>
      {!ready.users && <TablePlaceHolder />}
      {usersData.models !== null && (
        <TableWrapper>
          <UnitUserList
            handleModalStates={handleModalStates}
            setEditUserModalReady={setEditUserModalReady}
            patients={usersData.models}
            onSortedChange={onUserSortedChange}
            onPageChange={onUserPageChange}
            page={usersState.meta.page}
            total={usersData.meta.total}
            defaultPageSize={usersState.meta.limit}
          />
        </TableWrapper>
      )}
      <AddUser
        modalReady={addUserModalReady}
        handleModalStates={handleModalStates}
        unitId={user.unitId}
        onClose={() => setAddUserModalReady(false)}
      ></AddUser>
      {!isEmptyObject(editUserModalReady) && (
        <EditUser
          handleModalStates={handleModalStates}
          modalReady={editUserModalReady}
          user={editUserModalReady}
          unitId={user.unitId}
          onClose={() => setEditUserModalReady({})}
        ></EditUser>
      )}
    </Layout>
  )
}

const TableWrapper = styled.div`
  white-space: nowrap;
`
const HeaderWrapper = styled.div`
  margin-right: 50px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 0px 0px;
  grid-template-areas: '. .';
`

export default UnitsTabPanel
